// embedModuleStubs — embed-appropriate stubs for bare npm specifiers the legacy
// `system/platform` pages import at runtime.
//
// frank serializes project methods that `import X from 'prismjs'` / 'fuse.js'
// into a runtime `await import('prismjs')` inside the destringified function
// (scopeRewriter.js buildNpmModuleInjection), resolved registry-first via
// `globalThis.__SMBLS_PKGS__` then falling back to a bare `await import()`. In
// an embed iframe that bare specifier hits native ESM resolution with no
// import-map → `Failed to resolve module specifier 'prismjs'`.
//
// In a static embed tile, syntax highlighting (prismjs) and fuzzy search
// (fuse.js) are non-functional decorations, so a no-op stub is the correct
// degradation: the page renders (unhighlighted code, empty search) instead of
// throwing. Registered registry-first so a real module — if ever resolvable —
// still wins (additive; never overwrites an existing entry).

// --- prismjs ---------------------------------------------------------------
// Imported both as default (`import Prism from 'prismjs'`) and namespace; the
// API lives on both shapes so `__mod.default || __mod` and `Prism.highlight`
// both resolve.
// USER-CODE-ORIGIN: one self-contained factory, so the opaque user-code
// frame's bootstrap can carry it as TEXT (`makeEmbedModuleStubs.toString()`)
// and build the same registry inside the frame. No closure, no import.
export function makeEmbedModuleStubs() {
  var noop = function () {}
  var prismApi = {
    highlight: function (code) {
      return code
    },
    highlightElement: noop,
    highlightAll: noop,
    highlightAllUnder: noop,
    languages: {},
    hooks: { add: noop, run: noop },
    tokenize: function () {
      return []
    },
    plugins: {}
  }
  var PRISM_NS = Object.assign({}, prismApi, { default: prismApi })

  class FuseStub {
    constructor(list, options) {
      this._list = Array.isArray(list) ? list : []
      this._options = options || {}
    }

    search() {
      return []
    }
    setCollection(list) {
      this._list = Array.isArray(list) ? list : []
    }
    add() {}
    remove() {
      return []
    }
    removeAt() {}
    getIndex() {
      return {}
    }

    static createIndex() {
      return {}
    }
    static parseIndex() {
      return {}
    }
  }
  var FUSE_NS = { default: FuseStub, Fuse: FuseStub }

  return {
    prismjs: PRISM_NS,
    'fuse.js': FUSE_NS
  }
}

export const EMBED_MODULE_STUBS = makeEmbedModuleStubs()

// --- import-map fallback for LEGACY-serialized projects --------------------
// The __SMBLS_PKGS__ registry above only helps projects frank-serialized with
// the registry-first preamble. The `system/platform` project's data predates
// that change, so its methods emit a BARE `await import('prismjs')` that never
// consults __SMBLS_PKGS__ (verified in-browser: stubs registered, import still
// failed). An import-map in the iframe document resolves the bare specifier at
// the module-resolution layer regardless of serialization vintage. Each stub is
// a tiny ESM module exposed via a data: URL.
const ESM_SOURCES = {
  prismjs:
    'const P={highlight:c=>c,highlightElement(){},highlightAll(){},highlightAllUnder(){},languages:{},hooks:{add(){},run(){}},tokenize(){return[]},plugins:{}};' +
    'export default P;export const highlight=P.highlight;export const languages=P.languages;export const hooks=P.hooks;',
  'fuse.js':
    'class Fuse{constructor(l=[],o={}){this._list=Array.isArray(l)?l:[];this._options=o}' +
    'search(){return[]}setCollection(l=[]){this._list=Array.isArray(l)?l:[]}add(){}remove(){return[]}removeAt(){}getIndex(){return{}}' +
    'static createIndex(){return{}}static parseIndex(){return{}}}' +
    'export default Fuse;export{Fuse};'
}

const toDataUrl = (src) => 'data:text/javascript;charset=utf-8,' + encodeURIComponent(src)

// Build the <script type="importmap"> HTML to inject into the iframe document
// head (must precede any module import — written first in resetIframeDocument).
export function buildEmbedImportMap () {
  const imports = {}
  for (const spec in ESM_SOURCES) imports[spec] = toDataUrl(ESM_SOURCES[spec])
  return `<script type="importmap">${JSON.stringify({ imports })}</script>`
}

// Register the stubs on each target realm's `__SMBLS_PKGS__` map. Additive —
// existing entries (e.g. a host that already registered a real module) win.
// We register on BOTH the iframe realm (rerealmed functions run there) and the
// parent realm (plain functions that don't touch window/document run there),
// so the frank-emitted `globalThis.__SMBLS_PKGS__[path]` read resolves the stub
// regardless of which realm the destringified method executes in.
export function registerEmbedModuleStubs (...targets) {
  for (const target of targets) {
    if (!target) continue
    const store = target.__SMBLS_PKGS__ || (target.__SMBLS_PKGS__ = {})
    for (const name in EMBED_MODULE_STUBS) {
      if (!store[name]) store[name] = EMBED_MODULE_STUBS[name]
    }
  }
}

export default registerEmbedModuleStubs
